// Client-side helpers for the Menus page. Validation mirrors the API rules
// (apps/api/src/nav-menus.ts) so problems surface before the save round-trip.

export interface MenuItem {
  label: string;
  page?: string;
  url?: string;
  // One level of sub-menu (dropdown) items. A parent may also carry its own
  // link target, or act as a pure group heading (label + children, no target).
  children?: MenuItem[];
}

const PAGE_SLUG_PATTERN = /^[a-z0-9][a-z0-9-]*$/;
const SAFE_URL_PATTERN = /^(https?:\/\/|mailto:|tel:|\/)/i;
const MAX_ITEMS = 30;
const MAX_CHILD_ITEMS = 30;
const MAX_LABEL_LENGTH = 80;
const MAX_URL_LENGTH = 512;

// UI copy per menu slot. Keyed by the slugs the API defines.
export const MENU_HINTS: Record<string, string> = {
  header:
    "The main navigation at the top of every page. Add sub-links to an item to turn it into a dropdown.",
  footer: "Links shown at the bottom of every page — privacy policy, terms, and the like."
};

/** A menu item is in "page mode" when it links to a site page (vs a custom URL). */
export function isPageItem(item: MenuItem): boolean {
  return item.page !== undefined;
}

/** Which link target an item carries: a page, a custom URL, or none (a heading). */
export function linkMode(item: MenuItem): "page" | "url" | "none" {
  if (item.page !== undefined) return "page";
  if (item.url !== undefined) return "url";
  return "none";
}

/** True when the item has a non-empty sub-menu. */
export function hasChildren(item: MenuItem): boolean {
  return Array.isArray(item.children) && item.children.length > 0;
}

/** Label check shared by parents and children. */
function labelProblem(item: MenuItem): string {
  if (!item || typeof item.label !== "string" || !item.label.trim()) {
    return "Add a label.";
  }
  if (item.label.trim().length > MAX_LABEL_LENGTH) {
    return `Keep labels under ${MAX_LABEL_LENGTH} characters.`;
  }
  return "";
}

/** Link-target check for an item that must link somewhere. */
function targetProblem(item: MenuItem): string {
  if (isPageItem(item)) {
    const page = item.page ?? "";
    if (!PAGE_SLUG_PATTERN.test(page) || page.length > 50) {
      return "Choose a page.";
    }
    return "";
  }
  const url = (item.url ?? "").trim();
  if (!url) {
    return "Enter a link URL.";
  }
  if (url.length > MAX_URL_LENGTH || !SAFE_URL_PATTERN.test(url)) {
    return "Links must start with https://, http://, mailto:, tel: or /.";
  }
  return "";
}

/** Validate a sub-menu child — always a leaf that must link somewhere. */
function childProblem(child: MenuItem): string {
  if (hasChildren(child)) {
    return "Sub-menus can only be one level deep.";
  }
  return labelProblem(child) || targetProblem(child);
}

/**
 * Validate only the item's own label and link — not its sub-menu. Used for the
 * per-row hint so a parent's message reflects the parent, and each child row
 * reports its own problem.
 */
export function menuItemSelfProblem(item: MenuItem): string {
  const label = labelProblem(item);
  if (label) return label;
  // A parent that carries a link validates that link; a pure heading is fine
  // as long as it has at least one sub-item.
  if (linkMode(item) !== "none") return targetProblem(item);
  if (!hasChildren(item)) return "Add a link or a sub-item.";
  return "";
}

/** Validate an item and its sub-menu; "" when the whole thing is saveable. */
export function menuItemProblem(item: MenuItem): string {
  const self = menuItemSelfProblem(item);
  if (self) return self;

  const children = item.children ?? [];
  if (children.length > MAX_CHILD_ITEMS) {
    return `Keep sub-menus under ${MAX_CHILD_ITEMS} links.`;
  }
  for (const child of children) {
    const problem = childProblem(child);
    if (problem) return problem;
  }
  return "";
}

/** First problem across a whole menu, gating its Save button. */
export function menuProblem(items: MenuItem[]): string {
  if (items.length > MAX_ITEMS) {
    return `Menus are limited to ${MAX_ITEMS} items.`;
  }
  for (const item of items) {
    const problem = menuItemProblem(item);
    if (problem) return problem;
  }
  return "";
}

/** Reorder an item by delta positions; returns the input when out of range. */
export function moveItem<T>(items: T[], index: number, delta: number): T[] {
  const target = index + delta;
  if (index < 0 || index >= items.length || target < 0 || target >= items.length) {
    return items;
  }
  const next = items.slice();
  const [moved] = next.splice(index, 1);
  next.splice(target, 0, moved as T);
  return next;
}

/** Normalise a single leaf item to its payload shape (trimmed, one link field). */
function leafPayload(item: MenuItem): MenuItem {
  return isPageItem(item)
    ? { label: item.label.trim(), page: item.page }
    : { label: item.label.trim(), url: (item.url ?? "").trim() };
}

/** Normalise items to the API payload shape (trimmed link fields + children). */
export function toSavePayload(items: MenuItem[]): MenuItem[] {
  return items.map((item) => {
    const base: MenuItem =
      linkMode(item) === "none" ? { label: item.label.trim() } : leafPayload(item);
    const children = item.children ?? [];
    if (children.length > 0) {
      base.children = children.map(leafPayload);
    }
    return base;
  });
}
